/**
 * Dialog for loading saved wizard scenarios
 */

import { useState } from 'react';
import { useWizardPersistence } from '@/hooks/useWizardPersistence';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  FolderOpen, 
  Trash2, 
  Search, 
  Calendar,
  Users,
  Cpu,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface LoadScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScenarioLoaded?: () => void;
}

export function LoadScenarioDialog({ 
  open, 
  onOpenChange,
  onScenarioLoaded 
}: LoadScenarioDialogProps) {
  const { loading, savedScenarios, loadScenario, deleteScenario } = useWizardPersistence();
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleLoadScenario = async (scenarioId: string) => {
    try {
      await loadScenario(scenarioId);
      onOpenChange(false);
      onScenarioLoaded?.();
    } catch (err) {
      console.error('Failed to load scenario:', err);
    }
  };

  const handleDeleteScenario = async (scenarioId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setDeletingId(scenarioId);
      await deleteScenario(scenarioId);
    } catch (err) {
      console.error('Failed to delete scenario:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredScenarios = savedScenarios.filter((scenario) => {
    if (!searchQuery) return true;
    const name = (scenario.meta as any)?.name?.toLowerCase() || '';
    return name.includes(searchQuery.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Load Saved Configuration
          </DialogTitle>
          <DialogDescription>
            Choose a previously saved wizard configuration to continue where you left off.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search configurations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Scenarios List */}
        <div className="flex-1 overflow-y-auto space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredScenarios.length === 0 ? (
            <Card className="p-12 text-center">
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Saved Configurations</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'No configurations match your search.'
                  : 'Start a new wizard to create your first configuration.'}
              </p>
            </Card>
          ) : (
            filteredScenarios.map((scenario) => {
              const meta = scenario.meta as any;
              const environment = (scenario as any).environment || {};
              const personas = (scenario as any).personas || [];
              const modelSelections = (scenario as any).model_selections || {};
              
              return (
                <Card
                  key={scenario.id}
                  className="p-4 cursor-pointer hover:border-primary transition-colors"
                  onClick={() => handleLoadScenario(scenario.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1">{meta?.name || 'Unnamed Configuration'}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>
                          Updated {formatDistanceToNow(new Date(scenario.updated_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteScenario(scenario.id, e)}
                      disabled={deletingId === scenario.id}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {deletingId === scenario.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <Cpu className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <div className="font-medium">{modelSelections?.selectedModelIds?.length || 0}</div>
                        <div className="text-muted-foreground text-xs">Models</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-green-100 dark:bg-green-900 flex items-center justify-center">
                        <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <div className="font-medium">{personas.length || 0}</div>
                        <div className="text-muted-foreground text-xs">Personas</div>
                      </div>
                    </div>
                    <div>
                      <Badge variant="outline" className="text-xs">
                        {environment.preferredDeployment || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
